import React, { useEffect, useState } from 'react'
import { ArrowLeftRight, ArrowRight, RefreshCw, ShieldCheck, Wallet, Waves } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { get, short } from '../../api/client'

const tradeKind = row => String(row?.trade_type || '').includes('bridge') || row?.provider === 'lifi' ? 'Bridge' : 'Swap'

function ProviderCard({ icon, name, tag, text, online, onClick }) {
  return <button className="provider-card panel" onClick={onClick}>
    <div className="provider-icon">{icon}</div>
    <span>{tag}</span>
    <h3>{name}</h3>
    <p>{text}</p>
    <div><small className={online ? 'positive' : ''}>{online ? 'ONLINE' : 'CHECKING'}</small><ArrowRight /></div>
  </button>
}

function FlowNode({ name, token, active }) {
  return <div className={`chain ${active ? 'active' : ''}`}>
    <div>{name[0]}</div>
    <span><b>{name}</b><small>{token}</small></span>
  </div>
}

export default function TradeHubPage() {
  const navigate = useNavigate()
  const [providers, setProviders] = useState(null)
  const [health, setHealth] = useState(null)
  const [history, setHistory] = useState([])
  const [error, setError] = useState('')

  const load = async () => {
    setError('')
    const [p, h, t] = await Promise.allSettled([
      get('/api/v1/trade/providers'),
      get('/api/v1/trade/providers/health'),
      get('/api/v1/trade/history?limit=25')
    ])
    if (p.status === 'fulfilled') setProviders(p.value)
    if (h.status === 'fulfilled') setHealth(h.value)
    if (t.status === 'fulfilled') {
      const value = t.value
      setHistory(Array.isArray(value) ? value : value?.items || value?.transactions || [])
    }
    const failed = [p, h, t].find(result => result.status === 'rejected')
    if (failed) setError(failed.reason?.message || 'Trade status unavailable')
  }

  useEffect(() => { load() }, [])

  const swapOnline = Boolean(health?.providers?.jupiter?.online || providers?.providers?.jupiter?.quoteAvailable)
  const bridgeOnline = Boolean(health?.providers?.lifi?.online || providers?.providers?.lifi?.configured)

  return <div className="rose-trade-hub restored-trade-hub">
    <div className="page-intro rose-trade-hub-intro">
      <div>
        <div className="eyebrow"><Waves size={14} />Rose Trade</div>
        <h1>Swap and bridge from one place.</h1>
        <p>Trade Solana assets or move value between Solana and supported EVM networks through the Rose Marketplace experience.</p>
      </div>
      <div className="page-actions">
        <button className="primary" onClick={() => navigate('/swap')}><ArrowLeftRight />Open Rose Swap</button>
        <button className="secondary" onClick={() => navigate('/bridge')}><Waves />Open Rose Bridge</button>
      </div>
    </div>

    {error && <div className="trade-error">{error}</div>}

    <div className="trade-provider-grid rose-service-grid">
      <ProviderCard
        icon={<ArrowLeftRight />}
        name="Rose Swap"
        tag="Solana"
        text="Indexed token selection, wallet balances, live pricing and signed settlement."
        online={swapOnline}
        onClick={() => navigate('/swap')}
      />
      <ProviderCard
        icon={<Waves />}
        name="Rose Bridge"
        tag="Cross-chain"
        text="Solana and EVM wallet routing with destination control and settlement tracking."
        online={bridgeOnline}
        onClick={() => navigate('/bridge')}
      />
    </div>

    <div className="trade-layout rose-trade-overview-grid">
      <div className="panel trade-preview">
        <div className="panel-head">
          <div><h3>One Rose trade flow</h3><p>Select the action; Rose handles the correct network and wallet path.</p></div>
          <span className="mainnet-badge">MAINNET</span>
        </div>
        <div className="trade-flow">
          <FlowNode name="Source wallet" token="Solana or EVM" />
          <ArrowRight />
          <FlowNode name="Rose routing" token="Live quote" active />
          <ArrowRight />
          <FlowNode name="Destination" token="Selected wallet" />
        </div>
        <div className="route-cards">
          <div><span>Rose Swap</span><b>{swapOnline ? 'Online' : 'Checking'}</b><small>Solana token exchange</small></div>
          <div><span>Rose Bridge</span><b>{bridgeOnline ? 'Online' : 'Checking'}</b><small>Solana and EVM networks</small></div>
          <div><span>Default destination</span><b>ROSE</b><small>OnlyRose on Solana</small></div>
        </div>
      </div>

      <div className="panel rose-trade-uses">
        <div className="panel-head"><div><h3>Live uses</h3><p>What Rose Trade supports</p></div></div>
        <div className="use-list">
          <div><Wallet /><span><b>Fund Solana</b><small>Move value from an EVM wallet to a chosen Solana recipient.</small></span></div>
          <div><ShieldCheck /><span><b>Receive ROSE</b><small>ROSE is preselected as the destination token for Solana trades.</small></span></div>
          <div><Waves /><span><b>Move across networks</b><small>Choose the source and destination while Rose keeps wallet namespaces separate.</small></span></div>
        </div>
      </div>
    </div>

    <div className="panel trade-history-panel">
      <div className="panel-head">
        <div><h3>Trade history</h3><p>Quotes and transactions attached to the verified Rose account</p></div>
        <button className="secondary" onClick={load}><RefreshCw />Refresh</button>
      </div>
      {history.length ? <div className="trade-history-list">
        {history.map((row, index) => <div key={row.id || index}>
          <span className={`trade-state ${row.transaction_status || row.status || ''}`}>{row.transaction_status || row.status || 'unknown'}</span>
          <b>{tradeKind(row)}</b>
          <small>{short(row.input_token || '')} → {short(row.output_token || '')} · {row.network || '—'}</small>
          {row.source_tx_hash && <code>{short(row.source_tx_hash)}</code>}
        </div>)}
      </div> : <div className="trade-empty">No submitted Rose transactions yet.</div>}
    </div>
  </div>
}
